import React, { useRef, useState } from 'react';
import { Form, useForm, useFormState } from 'react-final-form';
import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  IconButton,
  Input,
  Stack, Text, useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import axios from 'axios';
import { useQueryClient } from 'react-query';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useHistory, useParams } from 'react-router-dom';
import { CloseIcon } from '@chakra-ui/icons';
import { ValidationErrors } from 'final-form';
import { NIL } from 'uuid';
import { Project } from '../../models/project';
import useContacts from '../../hooks/useContacts';
import { ContactDto } from '../../models/contactInfo';
import TextInputControl from './TextInput';
import SelectInputControl from './SelectInput';
import AutoSaveField from './AutoSaveField';
import FormActionBar from './FormActionBar';
import useProject from '../../hooks/useProject';
import CustomerProjectManagerField from './CustomerProjectManagerField';
import apiErrorMessage from '../../utils/apiError';
import useEditLock from '../../hooks/useEditLock';
import EditLockBanner from '../EditLockBanner';
import PlacesAttach from './PlacesAutocomplete';
import IconProjects from '../../assets/Icon-Projects.svg';
import IconProjectsWhite from '../../assets/Icon-Projects-White.svg';
import PageHeader from '../PageHeader';
import FormBreadcrumbs from '../FormBreadcrumbs';
import FormColumnHeading from './FormColumnHeading';
import BrandIcon from '../BrandIcon';
import ProjectClassificationFields from '../ProjectClassificationFields';
// item 187 (2026-06-26) — project leads + developing partner moved OFF the project:
// leads are assigned per-proposal in the approval wizard; the developing partner is
// automatic (per-user rate). ProjectLeadsField / DevelopingPartnerField no longer used here.

// item 939 (Chad) — MOVE a project to another customer, straight from the Customer dropdown.
// The dropdown was previously a silent no-op on a saved project (the update action never touched
// CustomerId). Now: picking a different customer pops a confirm; Confirm calls the dedicated
// move endpoint (project + work orders + invoices + subscriptions + contracts + proposals all
// follow; frozen documents keep their rendered content); Cancel snaps the dropdown back.
const MoveCustomerGuard: React.FC<{
  projectId: string;
  savedCustomerId?: string;
  customers: ContactDto[];
  locked: boolean;
}> = ({
  projectId, savedCustomerId, customers, locked,
}) => {
  const form = useForm();
  const { values } = useFormState({ subscription: { values: true } });
  const picked = (values as { customerId?: string })?.customerId || '';
  const toast = useToast();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const isMove = projectId !== NIL && !locked && !!savedCustomerId && !!picked
    && picked !== savedCustomerId;
  const fromName = customers.find((c) => c.id === savedCustomerId)?.name;
  const toName = customers.find((c) => c.id === picked)?.name;

  const cancel = () => form.change('customerId', savedCustomerId);
  const confirm = async () => {
    setBusy(true);
    try {
      const apiUrl = process.env.REACT_APP_BASIX_API || '';
      await axios.post(`${apiUrl}Project/${projectId}/move-customer`, { customerId: picked });
      await Promise.all([
        queryClient.invalidateQueries('project'),
        queryClient.invalidateQueries('projects'),
        queryClient.invalidateQueries('proposals'),
        queryClient.invalidateQueries('workOrders'),
        queryClient.invalidateQueries('invoices'),
        queryClient.invalidateQueries('contracts'),
        queryClient.invalidateQueries('recurringRevenues'),
      ]);
      toast({
        title: 'Project moved',
        description: toName ? `This project and all its documents now belong to ${toName}.` : undefined,
        status: 'success',
        duration: 6000,
        isClosable: true,
      });
    } catch (e) {
      form.change('customerId', savedCustomerId);
      toast({
        title: 'Move failed',
        description: apiErrorMessage(e, 'The project was NOT moved.'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog isOpen={isMove} leastDestructiveRef={cancelRef} onClose={cancel} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">Move this project?</AlertDialogHeader>
          <AlertDialogBody>
            <Text fontSize="sm">
              {'Move this project '}
              {fromName ? (
                <>
                  {'from '}
                  <b>{fromName}</b>
                  {' '}
                </>
              ) : ''}
              {'to '}
              <b>{toName || 'the selected customer'}</b>
              ?
            </Text>
            <Text fontSize="sm" mt={2}>
              Everything under it follows — proposals, work orders, invoices, subscriptions, and
              contracts. Finalized documents keep their existing content; editable proposals re-pull
              the new customer&apos;s legal/signatory info. Customer-staff PM picks are cleared.
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={cancel} variant="ghost">Cancel</Button>
            <Button colorScheme="purple" onClick={confirm} ml={3} isLoading={busy}>
              Move project
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

MoveCustomerGuard.defaultProps = { savedCustomerId: undefined };

// item 207 — write a parsed Google place / a customer's address into the project's service
// address. `form` is typed any: react-final-form's change() doesn't type dotted field paths.
const applyServiceAddress = (form: any, addr: any) => {
  form.batch(() => {
    form.change('serviceAddress.street', addr?.street || '');
    form.change('serviceAddress.unit', addr?.unit || '');
    form.change('serviceAddress.city', addr?.city || '');
    form.change('serviceAddress.state', addr?.state || '');
    form.change('serviceAddress.zip', addr?.zip || '');
    form.change('serviceAddress.country', addr?.country || '');
  });
};

// item 830 (Chad) — the legal "Property official address" is now a broken-out Address (street/
// city/state/zip), consistent with the service address. Same batched write as above.
const applyLegalAddress = (form: any, addr: any) => {
  form.batch(() => {
    form.change('legalAddress.street', addr?.street || '');
    form.change('legalAddress.unit', addr?.unit || '');
    form.change('legalAddress.city', addr?.city || '');
    form.change('legalAddress.state', addr?.state || '');
    form.change('legalAddress.zip', addr?.zip || '');
    form.change('legalAddress.country', addr?.country || '');
  });
};

// item 830 (Chad) — "Copy from project": pull the project's own name + service address down into
// the legal (owning entity) fields, so a property like "Palms Resort Las Vegas" fills in one click.
const copyFromProject = (form: any) => {
  const v = form.getState().values;
  form.batch(() => {
    if (v?.name) form.change('legalName', v.name);
    applyLegalAddress(form, v?.serviceAddress);
  });
};

// item 597 (REVERSES 570/578) — Platform no longer lives on the PROJECT; it originates on
// the proposal / standalone work order and flows down the document chain. The project's
// Platform field (and its inline validation) were removed here.

const ProjectForm: React.FC = () => {
  const { id } = useParams<{id: string}>();
  const { data, setProject } = useProject({ id });
  const { data: contacts, status: contactsStatus } = useContacts();
  const history = useHistory();

  const projectIcon = useColorModeValue(IconProjects, IconProjectsWhite);
  const toast = useToast();
  // item 139: a Closed/Archived project is locked server-side — make the form read-only
  // (Reopen to edit) so the user never fills it out and hits a save error.
  const locked = Boolean(data?.isLocked);
  // item 213 — pessimistic edit-lock: another user editing this project → read-only + Take Over
  const { readOnly: lockedByOther, holderName: lockHolder, takeOver: lockTakeOver } = useEditLock('PROJECT', id);

  const onSubmit = async (values: Project) => {
    if (lockedByOther) {
      toast({
        title: 'Locked',
        description: `${lockHolder || 'Another user'} is editing this project. Take over before saving.`,
        status: 'warning',
        duration: 6000,
        isClosable: true,
      });
      return;
    }
    try {
      const savedId = await setProject(values);
      // item 826 (Chad) — a NEW project lands ON the project (its detail page), not back on
      // wherever the add came from (CRM). The create endpoint returns the new id; an edit
      // returns undefined → keep the go-back behavior.
      if (id === NIL && typeof savedId === 'string' && savedId && savedId !== NIL) {
        history.push(`/project/${savedId}`);
      } else {
        history.goBack();
      }
    } catch (e) {
      // item 139: surface the REAL reason (e.g. "It is forbidden to modify this project")
      // instead of a scary generic "Save failed".
      toast({
        title: 'Save failed',
        description: apiErrorMessage(e, 'Your changes were NOT saved. Please try again.'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      });
    }
  };

  // item 139: inline required-field validation — block submit + highlight the field
  // (the controls render the message) rather than a server round-trip.
  const validate = (values: Project): ValidationErrors => {
    const errors: any = {};
    if (!values?.name?.trim()) errors.name = 'Project name is required';
    const addr: any = values?.serviceAddress ?? {};
    const addrErrors: any = {};
    if (!addr.street?.trim()) addrErrors.street = 'Street is required';
    if (!addr.city?.trim()) addrErrors.city = 'City is required';
    if (!addr.state?.trim()) addrErrors.state = 'State is required';
    if (!addr.zip?.trim()) addrErrors.zip = 'Zip is required';
    if (Object.keys(addrErrors).length) errors.serviceAddress = addrErrors;
    // dart (2026-07-15, Chad) — NEXT_EPIC #2: legal name + official address required on a NEW project
    // (not enforced on existing projects so they stay editable without back-filling legal info).
    if (id === NIL) {
      if (!values?.legalName?.trim()) errors.legalName = 'Legal name is required';
      // item 830 — the legal address is now structured; require the same parts as the service address
      const la: any = values?.legalAddress ?? {};
      const laErrors: any = {};
      if (!la.street?.trim()) laErrors.street = 'Street is required';
      if (!la.city?.trim()) laErrors.city = 'City is required';
      if (!la.state?.trim()) laErrors.state = 'State is required';
      if (!la.zip?.trim()) laErrors.zip = 'Zip is required';
      if (Object.keys(laErrors).length) errors.legalAddress = laErrors;
    }
    return errors;
  };

  return (
    <>
      {/* item 353 — sticky page title: pins below the persistent dashboard while the
          form scrolls under it (Chad's ask; PageHeader owns the brand rule) */}
      {/* item 417 — trail back home */}
      <FormBreadcrumbs crumbs={[{ label: 'Projects', to: '/' }, { label: id !== NIL ? data?.name || 'Project' : 'New project' }]} />
      <PageHeader
        sticky
        compact
        icon={<BrandIcon src={IconProjects} color="currentColor" boxSize="18px" />}
        title={`${id !== NIL ? 'EDIT' : 'ADD'} PROJECT`}
        actions={(
          <IconButton
            variant="ghost"
            aria-label="back"
            onClick={() => history.goBack()}
            icon={<CloseIcon />}
          />
        )}
      />
      {locked && (
        <Alert status="warning" borderRadius="md" mt={4} fontSize="sm">
          <AlertIcon />
          This project is closed — reopen it (the Reopen button above) to make changes.
        </Alert>
      )}
      {/* item 213 — another user holds the edit lock: read-only + Take Over */}
      {lockedByOther && (
        <Box mt={4}>
          <EditLockBanner holderName={lockHolder} onTakeOver={lockTakeOver} />
        </Box>
      )}
      <Form
        onSubmit={onSubmit}
        initialValues={{ ...data }}
        validate={validate}
        keepDirtyOnReinitialize
        render={({ handleSubmit, form }) => (
          <form onSubmit={handleSubmit}>
            {/* item 939 — customer-move confirm (watches the Customer dropdown) */}
            <MoveCustomerGuard
              projectId={id}
              savedCustomerId={data?.customerId}
              customers={contacts?.index ?? []}
              locked={locked || lockedByOther}
            />
            <Flex direction={{ base: 'column', md: 'row' }} align="center">
              <FormColumnHeading
                heading="Project Information"
                subHeading="Information about the project"
              />
              <Box flexBasis="80%">
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4} justify="center">
                  <TextInputControl
                    name="name"
                    label="Project Name"
                    isRequired
                  />
                  <TextInputControl
                    name="projectId"
                    label="Project Number"
                    isRequired={false}
                    placeholder="Uses your standardized numbering — fill in to override with a custom number"
                  />
                </Stack>
              </Box>
            </Flex>
            <Divider my={4} />
            <Flex direction={{ base: 'column', md: 'row' }} align="center">
              <FormColumnHeading
                heading="Address"
                subHeading="Information for the location of the contact"
              />
              <Box flexBasis="80%">
                {/* item 221 (OVERRIDES item 215's separate-box call) — the business/place
                    Places lookup now attaches DIRECTLY onto the project NAME field (CRM-style),
                    so typing a business name there autofills the service address below. The
                    dedicated "Find a business or place" box was removed. */}
                <PlacesAttach
                  targetId="name"
                  mode="business"
                  onSelect={(place) => {
                    // item 328 — Google sets the place name on the DOM input only;
                    // react-final-form never heard it, so the SAVED name stayed whatever
                    // was typed ("Pal" vs the visible "Palms…"). Adopt the place name
                    // into form state too, same as the CRM contact form (207-C).
                    if (place.name) form.change('name', place.name);
                    applyServiceAddress(form, place);
                  }}
                />
                {/* item 207-B/208 — address autocomplete attached to the STREET field itself */}
                <PlacesAttach
                  targetId="serviceAddress.street"
                  mode="address"
                  onSelect={(place) => applyServiceAddress(form, place)}
                />
                <Stack direction={['column', 'row']} align="flex-start" spacing={4} py={4}>
                  <TextInputControl
                    name="serviceAddress.street"
                    label="Street"
                    isRequired
                  />
                  <TextInputControl
                    name="serviceAddress.unit"
                    label="Unit"
                    isRequired={false}
                  />
                </Stack>
                <Stack direction={['column', 'row']} align="flex-start" spacing={4} py={4}>
                  <TextInputControl
                    name="serviceAddress.city"
                    label="City"
                    isRequired
                  />
                  <TextInputControl
                    name="serviceAddress.state"
                    label="State"
                    isRequired
                  />
                  <TextInputControl
                    name="serviceAddress.zip"
                    label="Zip"
                    isRequired
                  />
                </Stack>
              </Box>
            </Flex>
            <Divider my={4} />
            {/* item 225 — this section describes the CUSTOMER's people, not Basix users:
                "Project PM Information". The old "Sales Rep" (Basix-user) dropdown is replaced
                by the customer's Project Manager (from the customer's CRM staff, item 206 —
                reuses item 219's CustomerProjectManagerField). The "Use customer's address"
                shortcut is removed (the project keeps its own service address + Places autofill
                above). project.salesRepId stays in the model (defaulted server-side to the
                creator, item 222) — it's just no longer picked here. NOTE (Chad): the separate
                "Artifact on the create project page" ask wasn't built — unclear what it meant. */}
            <Flex direction={{ base: 'column', md: 'row' }} align="center">
              <FormColumnHeading
                heading="Project PM Information"
                subHeading="The customer and their project manager for this project"
              />
              <Box flexBasis="80%">
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4} align="flex-start" py={4}>
                  <Box flex="1">
                    <SelectInputControl name="customerId" label="Customer">
                      {contactsStatus === 'success' && contacts
                  && contacts.index.filter((c) => !c.isVendor)
                  // eslint-disable-next-line max-len
                    .map((contact: ContactDto) => <option key={contact.id} value={contact.id}>{contact.name.toUpperCase()}</option>)}
                    </SelectInputControl>
                  </Box>
                  <Box flex="1">
                    {/* item 225 — the customer's project manager (from the customer's CRM staff) */}
                    <CustomerProjectManagerField />
                  </Box>
                </Stack>
                <TextInputControl
                  name="description"
                  label="Description"
                  isRequired={false}
                />
              </Box>
            </Flex>
            <Divider my={4} />
            {/* item 163 — managed classification pick-lists (Job Type / Brand / Runway …) */}
            <Flex direction={{ base: 'column', md: 'row' }} align="flex-start">
              <FormColumnHeading
                heading="Classifications"
                subHeading="Job type, brand, runway, and other managed tags (configured in Settings)"
              />
              <Box flexBasis="80%" py={4}>
                <ProjectClassificationFields projectId={id} />
              </Box>
            </Flex>
            <Divider my={4} />
            {/* dart (2026-07-15, Chad) — NEXT_EPIC #2: project-level legal info for future contracts.
                Legal name + official address are required on a NEW project; authorized person/email
                optional. (Not hard-required on existing projects so they stay editable.) */}
            <Flex direction={{ base: 'column', md: 'row' }} align="flex-start">
              <FormColumnHeading
                heading="Legal (for contracts)"
                subHeading="The owning corporate entity + authorized contact — used for future contracts."
              />
              <Box flexBasis="80%" py={4}>
                {/* item 830 (Chad) — one-click copy of the project's own name + address into the
                    owning-entity legal fields (e.g. "Palms Resort Las Vegas"). */}
                <Flex justify="flex-end" mb={2}>
                  <Button size="sm" variant="outline" onClick={() => copyFromProject(form)}>
                    Copy from project
                  </Button>
                </Flex>
                <TextInputControl name="legalName" label="Owning company / property legal name" isRequired={id === NIL} />
                {/* item 830 — property official address broken out to match the service address */}
                <PlacesAttach
                  targetId="legalAddress.street"
                  mode="address"
                  onSelect={(place) => applyLegalAddress(form, place)}
                />
                <Stack direction={['column', 'row']} align="flex-start" spacing={4} mt={3}>
                  <TextInputControl name="legalAddress.street" label="Street" isRequired={id === NIL} />
                  <TextInputControl name="legalAddress.unit" label="Unit" isRequired={false} />
                </Stack>
                <Stack direction={['column', 'row']} align="flex-start" spacing={4} mt={3}>
                  <TextInputControl name="legalAddress.city" label="City" isRequired={id === NIL} />
                  <TextInputControl name="legalAddress.state" label="State" isRequired={id === NIL} />
                  <TextInputControl name="legalAddress.zip" label="Zip" isRequired={id === NIL} />
                </Stack>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4} mt={3}>
                  <Box flex="1"><TextInputControl name="authorizedContactName" label="Authorized person / name (optional)" isRequired={false} /></Box>
                  <Box flex="1"><TextInputControl name="authorizedContactEmail" label="Authorized email (optional)" isRequired={false} /></Box>
                </Stack>
                {/* item 830 (Chad) — the licensed-location portions field moved OFF the project;
                    it's captured on the proposal instead. */}
              </Box>
            </Flex>
            <Input type="hidden" value={data?.id} name="id" />
            {/* item 577 — shared action bar: the save cluster (badge + Cancel + Save)
                anchors together on the right and never separates */}
            <FormActionBar
              save={(
                <>
                  <AutoSaveField
                    enabled={id !== NIL && id !== '' && !locked && !lockedByOther}
                    save={(v) => setProject(v)}
                    canSave={(v) => { const e = validate(v); return !e || Object.keys(e).length === 0; }}
                  />
                  <Button variant="outline" onClick={() => history.goBack()}>Cancel</Button>
                  <Button type="submit" flexShrink={0} isDisabled={locked || lockedByOther}>Save</Button>
                </>
              )}
            />
          </form>
        )}
      />
    </>
  );
};

export default withAuthenticationRequired(ProjectForm);
